const SESSION_KEY = 'packproof.portal.cognito.session.v1';
const LOGIN_STATE_KEY = 'packproof.portal.cognito.login.v1';
const REFRESH_SKEW_MS = 60_000;

export type PortalCognitoSession = {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
};

type PortalCognitoConfig = {
  clientId: string;
  domain: string;
  redirectUri: string;
  logoutUri: string;
};

type StoredLogin = {
  state: string;
  verifier: string;
  returnTo: string;
};

function validToken(value: unknown): value is string {
  return typeof value === 'string' && value.length > 20 && value.length <= 16_384;
}

function config(): PortalCognitoConfig {
  const clientId = String(import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID ?? '').trim();
  const domain = String(import.meta.env.VITE_COGNITO_MANAGED_LOGIN_BASE_URL ?? '').trim().replace(/\/$/, '');
  const redirectUri = String(import.meta.env.VITE_COGNITO_CALLBACK_URL ?? '').trim() || `${window.location.origin}/auth/callback`;
  const logoutUri = String(import.meta.env.VITE_COGNITO_LOGOUT_URL ?? '').trim() || `${window.location.origin}/sign-in`;
  if (!clientId || !domain || !/^https:\/\//i.test(domain)) {
    throw new Error('This portal build does not have a valid PackProof Cognito configuration.');
  }
  try {
    const parsed = new URL(redirectUri);
    const local = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    if (parsed.protocol !== 'https:' && !local) {
      throw new Error('invalid');
    }
  } catch {
    throw new Error('The PackProof portal Cognito callback must be an HTTPS origin path.');
  }
  return { clientId, domain, redirectUri, logoutUri };
}

export function cognitoEnabled(): boolean {
  const clientId = String(import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID ?? '').trim();
  const domain = String(import.meta.env.VITE_COGNITO_MANAGED_LOGIN_BASE_URL ?? '').trim();
  return Boolean(clientId && domain && /^https:\/\//i.test(domain));
}

function randomUrlToken(bytes = 32): string {
  const values = new Uint8Array(bytes);
  crypto.getRandomValues(values);
  return btoa(String.fromCharCode(...values)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function tokenExpiry(expiresInSeconds: unknown): number {
  const expiresIn = typeof expiresInSeconds === 'number' && Number.isFinite(expiresInSeconds)
    ? Math.max(60, expiresInSeconds) * 1_000
    : 3_600_000;
  return Date.now() + expiresIn;
}

function validateSession(value: unknown): PortalCognitoSession | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as Partial<PortalCognitoSession>;
  if (!validToken(input.accessToken) || !validToken(input.idToken) || !validToken(input.refreshToken)) return null;
  if (typeof input.accessTokenExpiresAt !== 'number' || !Number.isFinite(input.accessTokenExpiresAt)) return null;
  return {
    accessToken: input.accessToken,
    idToken: input.idToken,
    refreshToken: input.refreshToken,
    accessTokenExpiresAt: input.accessTokenExpiresAt,
  };
}

function persist(session: PortalCognitoSession): PortalCognitoSession {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

export function loadPortalCognitoSession(): PortalCognitoSession | null {
  const encoded = sessionStorage.getItem(SESSION_KEY);
  if (!encoded) return null;
  try {
    return validateSession(JSON.parse(encoded));
  } catch {
    return null;
  }
}

export function clearPortalCognitoSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(LOGIN_STATE_KEY);
}

async function exchangeToken(body: URLSearchParams): Promise<PortalCognitoSession> {
  const { domain } = config();
  const response = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded', accept: 'application/json' },
    body,
  });
  const payload = await response.json().catch(() => ({})) as {
    access_token?: unknown;
    id_token?: unknown;
    refresh_token?: unknown;
    expires_in?: unknown;
  };
  if (!response.ok) throw new Error('PackProof could not complete Cognito sign-in.');
  const refreshToken = validToken(payload.refresh_token) ? payload.refresh_token : loadPortalCognitoSession()?.refreshToken;
  const session = validateSession({
    accessToken: payload.access_token,
    idToken: payload.id_token,
    refreshToken,
    accessTokenExpiresAt: tokenExpiry(payload.expires_in),
  });
  if (!session) throw new Error('PackProof received an incomplete Cognito session.');
  return persist(session);
}

export async function startCognitoLogin(returnTo = '/'): Promise<void> {
  const { clientId, domain, redirectUri } = config();
  const state = randomUrlToken(24);
  const verifier = randomUrlToken(48);
  const challenge = await pkceChallenge(verifier);
  const login: StoredLogin = { state, verifier, returnTo: returnTo.startsWith('/') ? returnTo : '/' };
  sessionStorage.setItem(LOGIN_STATE_KEY, JSON.stringify(login));
  const url = new URL(`${domain}/oauth2/authorize`);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
}

export async function completeCognitoLogin(search: URLSearchParams): Promise<string> {
  const storedRaw = sessionStorage.getItem(LOGIN_STATE_KEY);
  sessionStorage.removeItem(LOGIN_STATE_KEY);
  const stored = storedRaw ? JSON.parse(storedRaw) as StoredLogin : null;
  const error = search.get('error_description') || search.get('error');
  if (error) throw new Error(error);
  const code = search.get('code')?.trim() ?? '';
  const state = search.get('state')?.trim() ?? '';
  if (!stored || !code || stored.state !== state) throw new Error('This PackProof sign-in callback is invalid or expired.');
  const { clientId, redirectUri } = config();
  await exchangeToken(new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: stored.verifier,
  }));
  return stored.returnTo || '/';
}

async function refresh(session: PortalCognitoSession): Promise<PortalCognitoSession> {
  const { clientId } = config();
  return exchangeToken(new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: session.refreshToken,
  }));
}

export async function currentPortalAccessToken(options: { forceRefresh?: boolean } = {}): Promise<string> {
  let session = loadPortalCognitoSession();
  if (!session) throw new Error('Sign in to PackProof to continue.');
  if (options.forceRefresh || session.accessTokenExpiresAt <= Date.now() + REFRESH_SKEW_MS) {
    session = await refresh(session);
  }
  return session.accessToken;
}

export async function currentPortalIdToken(): Promise<string> {
  const session = loadPortalCognitoSession();
  if (!session) throw new Error('Sign in to PackProof to continue.');
  if (session.accessTokenExpiresAt <= Date.now() + REFRESH_SKEW_MS) {
    return (await refresh(session)).idToken;
  }
  return session.idToken;
}

export function startCognitoLogout(): void {
  const { clientId, domain, logoutUri } = config();
  clearPortalCognitoSession();
  const url = new URL(`${domain}/logout`);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('logout_uri', logoutUri);
  window.location.assign(url.toString());
}
